import { MenuItem, Menu } from '@mui/material';
import { formatEnum } from '~/shared/formatters';
import { ColoredIcon } from '~/components/ColoredIcon/ColoredIcon';
import React from 'react';
import { useIntl } from 'react-intl';
import makeStyles from '@mui/styles/makeStyles';
import { Button } from 'components';
import classNames from 'classnames';

export enum QuerySortDropdownEnum {
    RECENT = 'RECENT',
    RELEVANT = 'RELEVANT'
}

export const sortQueryDropdownMenuOptions = [
    { id: QuerySortDropdownEnum.RELEVANT, localeId: 'catalog.search-media.relevance' },
    { id: QuerySortDropdownEnum.RECENT, localeId: 'catalog.sort-by.date' }
];

export const getQueryDropdownSortOrder = (order?: QuerySortDropdownEnum) => {
    if (order === QuerySortDropdownEnum.RELEVANT) {
        return undefined;
    }
    return ['-startDateTime'];
};

const useStyles = makeStyles((theme) => ({
    button: {
        ...theme.typography.body1,
        padding: theme.spacing(1, 2),
        letterSpacing: 'normal',
        color: theme.palette.primary.main,
        fontWeight: 400,
        height: 40,
        fontSize: 14,
        whiteSpace: 'nowrap',
        background: 'white',
        gap: 4,
        ...theme.border({ color: theme.palette.body.light, radius: 6 }),
        '&:hover': {
            background: theme.palette.body.light
        },
        [theme.breakpoints.down('md')]: {
            width: '100%'
        }
    },
    sortIcon: {
        backgroundColor: '#59748D',
        width: 24,
        height: 24
    },
    expandIcon: {
        width: 24,
        height: 24,
        color: theme.palette.primary.main,
        transition: theme.transitions.create('transform')
    },
    expandedIcon: {
        transform: 'rotate(180deg)'
    }
}));

type Props = {
    sortOrder: QuerySortDropdownEnum;
    handleSortOrderChange: (s: QuerySortDropdownEnum) => void;
};

export const QuerySortDropdown = ({ sortOrder, handleSortOrderChange }: Props) => {
    const classes = useStyles();
    const { formatMessage, messages } = useIntl();

    const handleSortOrderSelected = (newSortOrder: QuerySortDropdownEnum) => {
        handleMenuClose();
        handleSortOrderChange(newSortOrder);
    };

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const isDropdownOpen = Boolean(anchorEl);
    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const translationRecent = formatEnum(QuerySortDropdownEnum.RECENT, {
        messages,
        options: sortQueryDropdownMenuOptions
    });
    const translationRelevant = formatEnum(QuerySortDropdownEnum.RELEVANT, {
        messages,
        options: sortQueryDropdownMenuOptions
    });

    return (
        <>
            <Button
                id="choose-sort-order"
                aria-controls={isDropdownOpen ? 'sort-order-dropdown' : undefined}
                aria-haspopup="true"
                aria-expanded={isDropdownOpen ? 'true' : undefined}
                onClick={handleMenuOpen}
                className={classes.button}
                unstyled
            >
                <ColoredIcon
                    url="https://bk-public-prod.storage.googleapis.com/public/static/icon-sort.svg"
                    className={classes.sortIcon}
                />
                {formatMessage({ id: 'ondemand.handouts.sort' })}
                <img
                    className={classNames(classes.expandIcon, {
                        [classes.expandedIcon]: isDropdownOpen
                    })}
                    src="https://bk-public-prod.storage.googleapis.com/public/static/arrow-down-type-2.svg"
                    alt=""
                />
            </Button>
            <Menu
                id="sort-order-dropdown"
                anchorEl={anchorEl}
                open={isDropdownOpen}
                onClose={handleMenuClose}
                PaperProps={{
                    style: {
                        width: anchorEl ? anchorEl.clientWidth : undefined
                    }
                }}
            >
                <MenuItem
                    disabled={sortOrder === QuerySortDropdownEnum.RELEVANT}
                    onClick={() => handleSortOrderSelected(QuerySortDropdownEnum.RELEVANT)}
                    value={QuerySortDropdownEnum.RELEVANT}
                >
                    {translationRelevant}
                </MenuItem>
                <MenuItem
                    disabled={sortOrder === QuerySortDropdownEnum.RECENT}
                    onClick={() => handleSortOrderSelected(QuerySortDropdownEnum.RECENT)}
                    value={QuerySortDropdownEnum.RECENT}
                >
                    {translationRecent}
                </MenuItem>
            </Menu>
        </>
    );
};
